import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  ContactPhone,
  Save,
  Edit,
  Close
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contactService } from '../../services/contactService';
import { useAuth } from '../../contexts/AuthContext';

const ContactManagement = () => {
  const { user, isAdmin } = useAuth();
  const [formData, setFormData] = useState({
    instagram: '',
    facebook: '',
    whatsapp: '',
    email: ''
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const queryClient = useQueryClient();

  console.log('ContactManagement - User:', user, 'isAdmin:', isAdmin);

  // Buscar contatos
  const { data: contactsData, isLoading, error } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => contactService.getContacts(),
    retry: 1,
    refetchOnWindowFocus: false,
  });


  // Efeito para carregar dados quando a query for bem-sucedida
  useEffect(() => {
    if (contactsData?.data?.data?.contact) {
      const newFormData = {
        instagram: contactsData.data.data.contact.instagram || '',
        facebook: contactsData.data.data.contact.facebook || '',
        whatsapp: contactsData.data.data.contact.whatsapp || '',
        email: contactsData.data.data.contact.email || ''
      };
      setFormData(newFormData);
    }
  }, [contactsData]);

  // Mutation para atualizar contatos
  const updateContactsMutation = useMutation({
    mutationFn: (data) => {
      return contactService.updateContacts(data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(['contacts']);
      queryClient.refetchQueries(['contacts']);
      setSnackbar({ open: true, message: 'Contatos atualizados com sucesso!', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.error || error.message || 'Erro ao atualizar contatos', 
        severity: 'error' 
      });
    }
  });

  const handleInputChange = (field) => (event) => {
    const newValue = event.target.value;
    setFormData({
      ...formData,
      [field]: newValue
    });
  };

  const handleSubmit = () => {
    updateContactsMutation.mutate(formData);
    setModalOpen(false);
  };

  const handleOpenModal = () => {
    // Carregar dados atuais do banco
    if (contactsData?.data?.data?.contact) {
      setFormData({
        instagram: contactsData.data.data.contact.instagram || '',
        facebook: contactsData.data.data.contact.facebook || '',
        whatsapp: contactsData.data.data.contact.whatsapp || '',
        email: contactsData.data.data.contact.email || ''
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: '#9D00FF' }} />
      </Box>
    );
  }

  if (error) {
    console.error('❌ Erro na query de contatos:', error);
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Alert severity="error">
          Erro ao carregar contatos: {error.message}
          {error.response?.data?.error && (
            <Box mt={1}>
              <Typography variant="body2">
                Detalhes: {error.response.data.error}
              </Typography>
            </Box>
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <ContactPhone sx={{ color: '#9D00FF', mr: 2, fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#9D00FF' }}>
              Contatos da Rádio
            </Typography>
          </Box>

          {/* Exibir dados atuais */}
          <Paper sx={{ p: 3, bgcolor: 'rgba(10, 10, 10, 0.3)', mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" sx={{ color: '#9D00FF', fontWeight: 600 }}>
                Contatos Atuais
              </Typography>
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={handleOpenModal}
                sx={{
                  bgcolor: '#9D00FF',
                  '&:hover': { bgcolor: '#C733FF' }
                }}
              >
                Editar Contatos
              </Button>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: '#00D4FF', fontWeight: 600 }}>
                  Instagram:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {contactsData?.data?.data?.contact?.instagram || 'Não informado'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: '#00D4FF', fontWeight: 600 }}>
                  Facebook:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {contactsData?.data?.data?.contact?.facebook || 'Não informado'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: '#00D4FF', fontWeight: 600 }}>
                  WhatsApp:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {contactsData?.data?.data?.contact?.whatsapp || 'Não informado'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: '#00D4FF', fontWeight: 600 }}>
                  Email:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {contactsData?.data?.data?.contact?.email || 'Não informado'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Modal de Edição */}
          <Dialog 
            open={modalOpen} 
            onClose={handleCloseModal}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                bgcolor: 'rgba(20, 20, 20, 0.95)',
                color: 'white'
              }
            }}
          >
            <DialogTitle sx={{ color: '#9D00FF', fontWeight: 600 }}>
              Editar Contatos da Rádio
              <IconButton
                onClick={handleCloseModal}
                sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            
            <DialogContent>
              <Typography variant="h6" sx={{ color: '#9D00FF', mb: 3, fontWeight: 600 }}>
                Redes Sociais e Contatos
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Instagram"
                    value={formData.instagram}
                    onChange={handleInputChange('instagram')}
                    placeholder="https://instagram.com/nostalbeat"
                    helperText="URL completa do perfil do Instagram"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#9D00FF',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#9D00FF',
                      },
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Facebook"
                    value={formData.facebook}
                    onChange={handleInputChange('facebook')}
                    placeholder="https://facebook.com/nostalbeat"
                    helperText="URL completa da página do Facebook"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#9D00FF',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#9D00FF',
                      },
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="WhatsApp"
                    value={formData.whatsapp}
                    onChange={handleInputChange('whatsapp')}
                    placeholder="https://chat.whatsapp.com/XXXXXXXXXXXX"
                    helperText="Link de convite do grupo do WhatsApp"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#9D00FF',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#9D00FF',
                      },
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    placeholder="contato@nostalbeat.com"
                    helperText="Email de contato da rádio"
                    type="email"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#9D00FF',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#9D00FF',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={handleCloseModal}
                sx={{ color: 'white' }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSubmit}
                disabled={updateContactsMutation.isPending}
                sx={{
                  bgcolor: '#9D00FF',
                  '&:hover': { bgcolor: '#C733FF' }
                }}
              >
                {updateContactsMutation.isPending ? 
                  <CircularProgress size={20} color="inherit" /> : 'Salvar Contatos'
                }
              </Button>
            </DialogActions>
          </Dialog>

          <Box mt={3} p={2} sx={{ bgcolor: 'rgba(0, 212, 255, 0.1)', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: '#00D4FF', fontWeight: 600, mb: 1 }}>
              💡 Dicas:
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
              • Instagram: Use a URL completa do perfil (ex: https://instagram.com/nostalbeat)
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
              • Facebook: Use a URL completa da página (ex: https://facebook.com/nostalbeat)
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
              • WhatsApp: Use o link de convite do grupo (ex: https://chat.whatsapp.com/XXXXXXXXXXXX)
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              • Email: Use um email válido para contato da rádio
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactManagement;
